/**
 * Skeleton shell for public blog pages.
 * Keeps a stable first paint while the public content is loading.
 *
 * Accepts optional brand colors so cached visits avoid the white/blue flash
 * before the project data loads.
 */
interface SkeletonColors {
  menuBg?: string;
  menuFont?: string;
  heroBg?: string;
  heroFont?: string;
  gridBg?: string;
  font?: string;
}

export default function BlogLoadingSkeleton({
  variant = "grid",
  colors,
}: {
  variant?: "grid" | "post" | "category" | "materiais";
  colors?: SkeletonColors;
}) {
  const font = colors?.font || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  // Neutral dark fallbacks (not blue) so first visits don't clash with brand colors.
  const menuBg = colors?.menuBg || "#0f0f10";
  const menuFont = colors?.menuFont || "#e5e5e5";
  const heroBg = colors?.heroBg || "#0f0f10";
  const heroFont = colors?.heroFont || "#ffffff";
  const gridBg = colors?.gridBg || "#f5f5f5";
  const shimmerDark = {
    backgroundImage:
      "linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0.06) 100%)",
    backgroundSize: "800px 100%",
    animation: "fb-shimmer 1.4s ease-in-out infinite",
  } as const;
  const shimmerLight = {
    backgroundImage:
      "linear-gradient(90deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.12) 50%, rgba(0,0,0,0.06) 100%)",
    backgroundSize: "800px 100%",
    animation: "fb-shimmer 1.4s ease-in-out infinite",
  } as const;
  const navShim = isLight(menuBg) ? shimmerLight : shimmerDark;
  const heroShim = isLight(heroBg) ? shimmerLight : shimmerDark;
  const gridShim = isLight(gridBg) ? shimmerLight : shimmerDark;

  return (
    <main role="main" data-blog-shell="" style={{ fontFamily: font, backgroundColor: gridBg }} className="min-h-screen">
      {/* Barra de progresso indeterminada — sinaliza carregamento real */}
      <div className="fb-topbar" aria-hidden="true" />
      {/* Nav skeleton */}
      <nav
        style={{ backgroundColor: menuBg, color: menuFont, height: "64px", borderBottom: `1px solid ${menuFont}10` }}
        className="px-8 py-4 sticky top-0 z-50 backdrop-blur-md flex items-center"
      >
        <div className="h-8 w-28 rounded" style={navShim} />
        <div className="flex-1" />
        <div className="hidden md:flex gap-4">
          <div className="h-4 w-16 rounded" style={navShim} />
          <div className="h-4 w-20 rounded" style={navShim} />
          <div className="h-4 w-16 rounded" style={navShim} />
        </div>
      </nav>

      {/* Hero skeleton — decorative only; the real page owns the single H1. */}
      <section
        className="blog-hero px-6 py-20 md:py-28 text-center"
        style={{ background: heroBg, color: heroFont }}
      >
        <div className="h-14 md:h-16 w-full max-w-2xl mx-auto mb-4 rounded" style={heroShim} aria-hidden="true" />
        <div className="h-6 w-64 mx-auto rounded" style={heroShim} />
      </section>

      {/* Content skeleton */}
      <section style={{ background: gridBg }} className="px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {variant === "post" ? (
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="h-6 w-3/4 rounded" style={gridShim} />
              <div className="h-4 w-full rounded" style={gridShim} />
              <div className="h-4 w-full rounded" style={gridShim} />
              <div className="h-4 w-5/6 rounded" style={gridShim} />
              <div className="h-64 w-full rounded mt-6" style={gridShim} />
              <div className="h-4 w-full rounded" style={gridShim} />
              <div className="h-4 w-4/5 rounded" style={gridShim} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-lg overflow-hidden" style={{ background: isLight(gridBg) ? "#ffffff" : "rgba(255,255,255,0.05)" }}>
                  <div className="h-48" style={gridShim} />
                  <div className="p-5 space-y-3">
                    <div className="h-4 rounded w-3/4" style={gridShim} />
                    <div className="h-3 rounded w-full" style={gridShim} />
                    <div className="h-3 rounded w-1/2" style={gridShim} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

/** Lightweight luminance check for hex colors to pick contrasting skeleton tones. */
function isLight(hex?: string): boolean {
  if (!hex) return true;
  const m = hex.replace("#", "");
  if (m.length !== 3 && m.length !== 6) return true;
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // perceived luminance
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
}
